import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { supabase } from '../../lib/supabase';
import type { Conversation } from '../../types/conversations';
import { highlightContactInfo } from '../../utils/contactInfoHighlight';
import { ContactInfoBadge } from './ContactInfoBadge';
import { useSearchParams } from 'react-router-dom';

interface ConversationModalProps {
  conversationId: string | null;
  onClose: () => void;
}

export function ConversationModal({ conversationId, onClose }: ConversationModalProps) {
  const { chatbotPublicId } = useChatbot();
  const [searchParams] = useSearchParams();
  const urlChatbotId = searchParams.get('chatbot');
  
  // Utiliser l'ID du chatbot de l'URL s'il est disponible
  const activeChatbotId = urlChatbotId || chatbotPublicId;
  
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Style pour la barre de défilement personnalisée
  const scrollbarStyle = `
    .conversation-modal-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .conversation-modal-scrollbar::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
    }
    .conversation-modal-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(to bottom, rgba(59, 130, 246, 0.5), rgba(20, 184, 166, 0.5));
      border-radius: 8px;
    }
    .conversation-modal-scrollbar::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(to bottom, rgba(59, 130, 246, 0.7), rgba(20, 184, 166, 0.7));
    }
  `;

  // Ajouter/supprimer le style de la barre de défilement
  useEffect(() => {
    if (conversationId) {
      // Créer et ajouter la feuille de style
      const styleElement = document.createElement('style');
      styleElement.textContent = scrollbarStyle;
      document.head.appendChild(styleElement);
      
      // Nettoyer lors du démontage
      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [conversationId, scrollbarStyle]);

  useEffect(() => {
    if (!conversationId || !activeChatbotId) {
      setConversation(null);
      return;
    }

    const fetchConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Fetching conversation: ${conversationId} for chatbot: ${activeChatbotId}`);
        
        // Query conversations with additional security by filtering on activeChatbotId
        const { data, error } = await supabase
          .from('conversations')
          .select(`
            *,
            conversation_contact_info (
              id,
              type,
              value,
              message_id
            ),
            conversation_messages (*)
          `)
          .eq('id', conversationId)
          .eq('chatbot_public_id', activeChatbotId)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // No rows found
            setError('Conversation not found or you do not have access to view it');
          } else {
            throw error;
          }
          return;
        }
        
        setConversation(data);
      } catch (err) {
        console.error('Error fetching conversation:', err);
        setError('An error occurred while fetching the conversation');
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
  }, [conversationId, activeChatbotId]);

  if (!conversationId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/95 backdrop-blur-xl rounded-2xl w-full max-w-3xl h-[80vh] flex flex-col border border-white/10 shadow-xl shadow-black/30">
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-blue-600/10 to-teal-500/10">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Conversation from {conversation && format(new Date(conversation.created_at), 'PPP', { locale: fr })}
            </h2>
            {conversation?.conversation_contact_info && (
              <div className="flex flex-wrap gap-2 mt-2">
                {conversation.conversation_contact_info.map((info) => (
                  <ContactInfoBadge key={info.id} info={info} />
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center text-red-400 px-6 text-center">
            <p>{error}</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 conversation-modal-scrollbar">
            <div className="space-y-4">
              {conversation?.conversation_messages?.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'bot' ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 shadow-md ${
                      message.role === 'bot'
                        ? 'bg-gray-800/70 text-gray-200 border border-white/5'
                        : 'bg-gradient-to-r from-blue-600/80 to-teal-500/80 text-white'
                    }`}
                  >
                    <div 
                      className="whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightContactInfo(message.content) 
                      }}
                    />
                    <span className="text-xs opacity-60 mt-1 block">
                      {format(new Date(message.created_at), 'HH:mm')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}